import React from 'react';
import { useTranslation } from 'react-i18next';
import type { IRParam, IRValue } from '../types/ir';
import { paramDisplayLabel, defaultsForAll } from './paramHelpers';
import { Touchable } from './touchable';

interface PatternParamsCardProps {
  params: IRParam[];
  values: Record<string, IRValue>;
  onChange: (values: Record<string, IRValue>) => void;
  onValidityChange: (isValid: boolean) => void;
}

export const PatternParamsCard: React.FC<PatternParamsCardProps> = ({
  params,
  values,
  onChange,
  onValidityChange,
}) => {
  const { t } = useTranslation('builder');
  // Track the displayed string for each int field so a user can type partial
  // values (e.g. "" while clearing) without us round-tripping through NaN.
  const [intDrafts, setIntDrafts] = React.useState<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const p of params) {
      if (p.type === 'int') out[p.name] = String(values[p.name] ?? '');
    }
    return out;
  });

  const validity = React.useMemo(() => {
    for (const p of params) {
      if (p.type === 'int') {
        const draft = intDrafts[p.name] ?? '';
        if (!/^-?\d+$/.test(draft)) return false;
      }
    }
    return true;
  }, [params, intDrafts]);

  React.useEffect(() => {
    onValidityChange(validity);
  }, [validity, onValidityChange]);

  const setValue = (name: string, value: IRValue) => {
    onChange({ ...values, [name]: value });
  };

  const handleIntChange = (param: IRParam, raw: string) => {
    setIntDrafts((d) => ({ ...d, [param.name]: raw }));
    if (/^-?\d+$/.test(raw)) {
      setValue(param.name, parseInt(raw, 10));
    }
  };

  const reset = () => {
    const defaults = defaultsForAll(params);
    const newDrafts: Record<string, string> = {};
    for (const p of params) {
      if (p.type === 'int') newDrafts[p.name] = String(defaults[p.name]);
    }
    setIntDrafts(newDrafts);
    onChange(defaults);
  };

  if (params.length === 0) return null;

  return (
    <section
      aria-labelledby="pattern-params-heading"
      className="bg-card rounded-2xl border border-line shadow-sm overflow-hidden"
    >
      <header className="p-4 bg-primary-soft/60 border-b border-primary-soft flex items-center justify-between">
        <h3
          id="pattern-params-heading"
          className="text-[10px] font-black uppercase tracking-widest text-primary-strong"
        >
          {t('parameters.title', 'Parameters')}
        </h3>
        <Touchable
          haptic="softTap"
          onClick={reset}
          className="text-xs text-ink-faint hover:text-ink"
        >
          {t('paramsCard.resetToDefaults', 'Reset to defaults')}
        </Touchable>
      </header>
      <div className="p-4 space-y-3">
        {params.map((p) => {
          const labelText = paramDisplayLabel(p);
          const id = `param-${p.id}`;
          if (p.type === 'bool') {
            return (
              <label key={p.id} className="flex items-center gap-3" htmlFor={id}>
                <Touchable
                  haptic="transition"
                  id={id}
                  role="switch"
                  aria-checked={values[p.name] === true}
                  aria-label={labelText}
                  onClick={() => setValue(p.name, !(values[p.name] === true))}
                  className={`w-11 h-6 rounded-full border border-line transition-colors ${
                    values[p.name] === true ? 'bg-primary' : 'bg-soft'
                  }`}
                >
                  <span
                    className={`block w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                      values[p.name] === true ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </Touchable>
                <span className="text-sm font-bold text-ink">{labelText}</span>
              </label>
            );
          }
          if (p.type === 'int') {
            const draft = intDrafts[p.name] ?? '';
            const ok = /^-?\d+$/.test(draft);
            return (
              <label key={p.id} className="block" htmlFor={id}>
                <span className="text-[10px] font-bold uppercase tracking-widest text-ink-faint">
                  {labelText}
                </span>
                <input
                  id={id}
                  type="number"
                  inputMode="numeric"
                  aria-label={labelText}
                  value={draft}
                  onChange={(e) => handleIntChange(p, e.target.value)}
                  className="mt-1 block w-full px-3 py-2 bg-card border border-line rounded-xl text-base font-bold text-ink"
                />
                {!ok && (
                  <span className="block mt-1 text-xs text-danger">
                    {t('paramsCard.enterWholeNumber', 'Enter a whole number.')}
                  </span>
                )}
              </label>
            );
          }
          // text
          return (
            <label key={p.id} className="block" htmlFor={id}>
              <span className="text-[10px] font-bold uppercase tracking-widest text-ink-faint">
                {labelText}
              </span>
              <input
                id={id}
                type="text"
                aria-label={labelText}
                value={String(values[p.name] ?? '')}
                onChange={(e) => setValue(p.name, e.target.value)}
                className="mt-1 block w-full px-3 py-2 bg-card border border-line rounded-xl text-base font-bold text-ink"
              />
            </label>
          );
        })}
      </div>
    </section>
  );
};
